import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, Calendar, Clock, User, Phone, Mail,
  CheckCircle, XCircle, Wrench, Sparkles, ChevronLeft, ChevronRight,
  Plus, Search, Package, Download, Edit, Play, Users, 
  LogOut, ClipboardCheck, History, MessageSquare, Trophy, Star, Target,
  ThumbsUp, ThumbsDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import TherapistFeedback from './TherapistFeedback';
import TherapistPerformanceTab from './TherapistPerformanceTab';
import PinVerificationModal from '@/components/therapist/PinVerificationModal';
import RateYourCustomers from '@/components/therapist/RateYourCustomers';
import PerformanceDashboard from '@/components/therapist/PerformanceDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Room Status Colors (Dark variants as requested)
const roomStatusColors = {
  maintenance: 'bg-gray-500',      // Grey - Cannot book
  available: 'bg-green-700',       // Dark Green - Can book
  booked: 'bg-red-700',            // Dark Red - Reserved
  cleaning: 'bg-blue-700',         // Dark Blue - Temporarily unavailable
};

const roomStatusLabels = {
  maintenance: 'Under Maintenance',
  available: 'Available',
  booked: 'Booked',
  cleaning: 'Cleaning',
};

// Therapy types with TBD option
const therapyTypes = [
  { name: 'TBD - Decide on arrival', duration: 60 },
  { name: 'Head, Neck & Shoulder Therapy', duration: 30 },
  { name: 'Foot Reflexology', duration: 30 },
  { name: 'Champi Head Massage', duration: 30 },
  { name: 'Swedish Massage', duration: 60 },
  { name: 'Aromatherapy Massage', duration: 60 },
  { name: 'Deep Tissue Massage', duration: 60 },
  { name: 'Balinese Massage', duration: 60 },
  { name: 'Shirodhara Therapy', duration: 60 },
  { name: 'Royal Hot Stone Therapy', duration: 90 },
  { name: 'Signature Abhyanga Ritual', duration: 90 },
  { name: 'Couples Harmony Massage', duration: 90 },
  { name: 'Custom Massage', duration: 60 }
];

const TherapistOperations = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [futureBookings, setFutureBookings] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propertyTherapies, setPropertyTherapies] = useState([]); // Property-specific therapies
  
  // PIN Verification state
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [verifiedTherapist, setVerifiedTherapist] = useState(null);
  
  // Check for existing verified therapist on mount
  useEffect(() => {
    const storedTherapist = sessionStorage.getItem('verified_therapist');
    if (storedTherapist) {
      setVerifiedTherapist(JSON.parse(storedTherapist));
    } else {
      // Show PIN modal if not verified
      setPinModalOpen(true);
    }
  }, []);
  
  // Date filter only (no property filter for therapist)
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const assignedProperty = user?.assigned_property_id;
  const [propertyDetails, setPropertyDetails] = useState(null);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('operations');
  const [bookingsTab, setBookingsTab] = useState('today');
  
  // Dialogs
  const [recordServiceOpen, setRecordServiceOpen] = useState(false);
  const [futureBookingOpen, setFutureBookingOpen] = useState(false);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [bookingDetailsOpen, setBookingDetailsOpen] = useState(false);
  const [roomStatusDialogOpen, setRoomStatusDialogOpen] = useState(false);
  
  // Selected items
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Record Service Form
  const [recordStep, setRecordStep] = useState(1);
  const [searchPhone, setSearchPhone] = useState('');
  const [foundCustomer, setFoundCustomer] = useState(null);
  const [foundPackages, setFoundPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [bookingType, setBookingType] = useState('one_time');
  
  const [serviceForm, setServiceForm] = useState({
    guest_name: '',
    guest_gender: '',
    guest_phone: '',
    guest_email: '',
    guest_type: 'outside',
    therapy_type: '',
    therapy_duration: 60,
    therapist_id: '',
    room_id: '',
    booking_date: new Date().toISOString().split('T')[0],
    start_time: '',
    base_price: '',
    discount_percent: '0',
    payment_type: 'postpaid',
    payment_mode: '',
    payment_received_by: '',
    notes: ''
  });

  // Future Booking Form
  const [futureForm, setFutureForm] = useState({
    guest_name: '',
    guest_gender: '',
    guest_phone: '',
    guest_email: '',
    guest_type: 'outside',
    therapy_type: '',
    therapy_duration: 60,
    therapist_id: 'as_per_availability',
    room_id: '',
    booking_date: '',
    start_time: '',
    base_price: '',
    discount_percent: '0',
    payment_type: 'postpaid',
    payment_mode: '',
    payment_received_by: '',
    notes: ''
  });

  // Reschedule Form
  const [rescheduleForm, setRescheduleForm] = useState({
    new_date: '',
    new_time: ''
  });

  // Package Form (multi-step)
  const [packageStep, setPackageStep] = useState(1);
  const [packageForm, setPackageForm] = useState({
    package_name: '',
    guest_name: '',
    guest_gender: '',
    guest_phone: '',
    guest_email: '',
    therapy_type: 'TBD - Decide on arrival',
    total_sessions: '',
    session_duration: '60',
    total_price: '',
    validity_days: '90',
    guest_type: 'outside',
    therapist_id: 'as_per_availability',
    discount_percent: '0',
    payment_received_by: '',
    payment_mode: ''
  });

  // Service History filter
  const [serviceHistoryDate, setServiceHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Rate customer dialog state
  const [rateCustomerDialogOpen, setRateCustomerDialogOpen] = useState(false);
  const [selectedServiceForRating, setSelectedServiceForRating] = useState(null);
  const [ratingForm, setRatingForm] = useState({
    overall_rating: 0,
    behavior_rating: 0,
    punctuality_rating: 0,
    hygiene_rating: 0,
    would_serve_again: null,
    comment: ''
  });

  // Check if spa is currently open
  const isSpaOpen = useMemo(() => {
    // Default to open if hours not set
    if (!propertyDetails?.opening_time || !propertyDetails?.closing_time) {
      return true;
    }
    
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    
    const [openHour, openMin] = propertyDetails.opening_time.split(':').map(Number);
    const [closeHour, closeMin] = propertyDetails.closing_time.split(':').map(Number);
    
    const openingMinutes = openHour * 60 + (openMin || 0);
    let closingMinutes = closeHour * 60 + (closeMin || 0);
    
    // Handle overnight hours (e.g., 10:00 - 02:00)
    if (closingMinutes < openingMinutes) {
      // Spa closes after midnight
      return currentMinutes >= openingMinutes || currentMinutes < closingMinutes;
    }
    
    return currentMinutes >= openingMinutes && currentMinutes < closingMinutes;
  }, [propertyDetails]);

  const spaHoursDisplay = useMemo(() => {
    if (!propertyDetails?.opening_time || !propertyDetails?.closing_time) {
      return null;
    }
    return `${propertyDetails.opening_time} - ${propertyDetails.closing_time}`;
  }, [propertyDetails]);

  // Generate time slots
  const timeSlots = useMemo(() => {
    const slots = [];
    const startHour = propertyDetails?.opening_time ? parseInt(propertyDetails.opening_time.split(':')[0]) : 10;
    const endHour = propertyDetails?.closing_time ? parseInt(propertyDetails.closing_time.split(':')[0]) : 20;
    
    for (let h = startHour; h < endHour; h++) {
      slots.push(`${h.toString().padStart(2, '0')}:00`);
      slots.push(`${h.toString().padStart(2, '0')}:30`);
    }
    return slots;
  }, [propertyDetails]);

  // Fetch data on mount
  useEffect(() => {
    if (assignedProperty) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedProperty, selectedDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch property details
      const propResponse = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const prop = propResponse.data.find(p => p.hotel_name === assignedProperty);
      setPropertyDetails(prop);
      
      // Fetch property-specific therapies
      try {
        const therapiesResponse = await axios.get(
          `${API}/property-management/therapies/${encodeURIComponent(assignedProperty)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPropertyTherapies(therapiesResponse.data || []);
      } catch (err) {
        console.log('Could not fetch property therapies, using defaults');
        setPropertyTherapies([]);
      }
      
      // Fetch rooms
      const roomsResponse = await axios.get(`${API}/rooms?property_id=${assignedProperty}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(roomsResponse.data);
      
      // Fetch therapists for this property
      const therapistsResponse = await axios.get(`${API}/therapists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const propertyTherapists = therapistsResponse.data.filter(t => t.assigned_property_id === assignedProperty);
      setTherapists(propertyTherapists);
      
      // Fetch today's bookings
      const bookingsResponse = await axios.get(
        `${API}/bookings?property_id=${assignedProperty}&date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings(bookingsResponse.data);
      
      // Fetch future bookings
      const futureResponse = await axios.get(
        `${API}/bookings?property_id=${assignedProperty}&future=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFutureBookings(futureResponse.data.sort((a, b) => 
        new Date(a.booking_date || a.date) - new Date(b.booking_date || b.date)
      ));
      
      // Fetch services (for service history)
      const servicesResponse = await axios.get(
        `${API}/services?property_id=${assignedProperty}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setServices(servicesResponse.data);
      
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Combined therapy list: property-specific + fallback defaults
  const availableTherapies = useMemo(() => {
    if (propertyTherapies.length > 0) {
      return [
        { name: 'TBD - Decide on arrival', duration: 60, base_price: 0 },
        ...propertyTherapies.map(t => ({
          name: t.name,
          duration: t.duration,
          base_price: t.base_price
        }))
      ];
    }
    return therapyTypes;
  }, [propertyTherapies]);

  // Calculate final price with GST
  const calculatePrice = (basePrice, discountPercent) => {
    const base = parseFloat(basePrice) || 0;
    const discount = parseFloat(discountPercent) || 0;
    const discountAmount = base * (discount / 100);
    const net = base - discountAmount;
    const gst = net * 0.05;
    return {
      base,
      discountPercent: discount,
      discountAmount: discountAmount.toFixed(2),
      net,
      gst: gst.toFixed(2),
      total: (net + gst).toFixed(2)
    };
  };

  const servicePrice = useMemo(() => {
    return calculatePrice(serviceForm.base_price, serviceForm.discount_percent);
  }, [serviceForm.base_price, serviceForm.discount_percent]);

  const futurePrice = useMemo(() => {
    return calculatePrice(futureForm.base_price, futureForm.discount_percent);
  }, [futureForm.base_price, futureForm.discount_percent]);

  const packagePrice = useMemo(() => {
    return calculatePrice(packageForm.total_price, packageForm.discount_percent);
  }, [packageForm.total_price, packageForm.discount_percent]);

  // Search customer by phone
  const searchByPhone = async () => {
    if (!searchPhone || searchPhone.length < 10) {
      toast.error('Enter a valid phone number');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Check for active packages - handle errors gracefully
      let activePackages = [];
      try {
        const pkgResponse = await axios.get(
          `${API}/packages/search?phone=${searchPhone}&property_id=${assignedProperty}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Filter only active packages with remaining sessions
        activePackages = (pkgResponse.data || []).filter(pkg => 
          pkg.sessions_remaining > 0 || (pkg.total_sessions - (pkg.sessions_used || 0)) > 0
        );
      } catch (pkgError) {
        console.log('No packages found or error:', pkgError);
        activePackages = [];
      }
      setFoundPackages(activePackages);
      
      // Check for pre-booked appointments (today or future)
      const todayBookings = bookings.filter(b => 
        b.guest_phone === searchPhone && 
        b.status === 'scheduled'
      );
      
      // SCENARIO 1: Pre-booked one-time or package appointment exists
      if (todayBookings.length > 0) {
        const booking = todayBookings[0];
        setFoundCustomer(booking);
        setBookingType(booking.booking_type);
        
        // Pre-fill form from booking
        setServiceForm({
          ...serviceForm,
          guest_name: booking.guest_name,
          guest_gender: booking.guest_gender || '',
          guest_phone: booking.guest_phone,
          guest_email: booking.guest_email || '',
          guest_type: booking.guest_type || 'outside',
          therapy_type: booking.therapy_type,
          therapy_duration: booking.therapy_duration,
          room_id: booking.room_id,
          start_time: booking.start_time,
          base_price: booking.base_price?.toString() || '',
          discount_percent: booking.discount_percent?.toString() || '0',
          payment_type: booking.booking_type === 'package' ? 'prepaid' : (booking.payment_type || 'postpaid')
        });
        
        if (booking.booking_type === 'package') {
          // Find the associated package
          const pkg = activePackages.find(p => p.id === booking.package_id);
          if (pkg) {
            setSelectedPackage(pkg);
          }
          toast.success(`Pre-booked package session found! Session ${(pkg?.sessions_used || 0) + 1} of ${pkg?.total_sessions || '?'}`);
        } else {
          toast.success('Pre-booked one-time appointment found!');
        }
        
      // SCENARIO 2: Has active packages but no pre-booking - can avail package session
      } else if (activePackages.length > 0) {
        const pkg = activePackages[0];
        const sessionsUsed = pkg.sessions_used || 0;
        const sessionsRemaining = pkg.total_sessions - sessionsUsed;
        
        setSelectedPackage(pkg);
        setBookingType('package');
        setFoundCustomer(null); // No pre-booking, but has package
        
        // Pre-fill from package
        setServiceForm({
          ...serviceForm,
          guest_name: pkg.guest_name,
          guest_gender: pkg.guest_gender || '',
          guest_phone: pkg.guest_phone,
          guest_email: pkg.guest_email || '',
          therapy_type: pkg.therapy_type,
          therapy_duration: pkg.session_duration || 60,
          payment_type: 'prepaid', // Package is always prepaid
          base_price: '0', // No additional payment for package
          discount_percent: '0'
        });
        
        toast.success(`Active package found: ${pkg.package_name}\nSession ${sessionsUsed + 1}/${pkg.total_sessions} (${sessionsRemaining} remaining)`);
        
      // SCENARIO 3: No package, no pre-booking - ONLY one-time booking allowed
      } else {
        setFoundCustomer(null);
        setFoundPackages([]);
        setSelectedPackage(null);
        setBookingType('one_time'); // Force one-time only
        
        // Empty form with just phone
        setServiceForm({
          ...serviceForm,
          guest_phone: searchPhone,
          guest_name: '',
          guest_gender: '',
          guest_email: '',
          guest_type: 'outside',
          therapy_type: '',
          therapy_duration: 60,
          room_id: '',
          start_time: '',
          base_price: '',
          discount_percent: '0',
          payment_type: 'postpaid'
        });
        
        toast.info('New customer - Only one-time booking available. Create a package first for package sessions.');
      }
      
      setRecordStep(2);
    } catch (error) {
      console.error('Search error:', error);
      // Even if search fails, allow new customer booking
      setFoundCustomer(null);
      setFoundPackages([]);
      setSelectedPackage(null);
      setBookingType('one_time');
      setServiceForm({
        ...serviceForm,
        guest_phone: searchPhone,
        guest_name: '',
        guest_gender: '',
        guest_email: '',
        guest_type: 'outside',
        therapy_type: '',
        therapy_duration: 60,
        room_id: '',
        start_time: '',
        base_price: '',
        discount_percent: '0',
        payment_type: 'postpaid'
      });
      toast.info('New customer - proceeding with one-time booking form');
      setRecordStep(2);
    }
  };

  // Check if booking is for today and within time window
  const checkBookingValidity = () => {
    if (!foundCustomer) return { valid: true, message: '' };
    
    const bookingDate = foundCustomer.booking_date;
    const today = new Date().toISOString().split('T')[0];
    
    if (bookingDate !== today) {
      return { 
        valid: false, 
        message: `Therapy is scheduled for ${bookingDate}, not today. Would you like to reschedule?` 
      };
    }
    
    const bookingTime = foundCustomer.start_time;
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    // Check if within +-1 hour
    const bookingMinutes = parseInt(bookingTime.split(':')[0]) * 60 + parseInt(bookingTime.split(':')[1]);
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const diff = Math.abs(bookingMinutes - currentMinutes);
    
    if (diff > 60) {
      return { 
        valid: false, 
        message: `Therapy is scheduled for ${bookingTime}. Current time difference is ${Math.floor(diff/60)}h ${diff%60}m. Would you like to adjust?` 
      };
    }
    
    return { valid: true, message: '' };
  };

  // Submit service entry
  const handleServiceEntry = async () => {
    if (!serviceForm.guest_name || !serviceForm.guest_phone || !serviceForm.therapy_type || !serviceForm.room_id) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Calculate pricing
      const pricing = calculatePrice(serviceForm.base_price, serviceForm.discount_percent);
      
      // Create/update booking
      const payload = {
        property_id: assignedProperty,
        room_id: serviceForm.room_id,
        guest_name: serviceForm.guest_name,
        guest_gender: serviceForm.guest_gender,
        guest_phone: serviceForm.guest_phone,
        guest_email: serviceForm.guest_email,
        guest_type: serviceForm.guest_type,
        booking_type: bookingType,
        package_id: selectedPackage?.id || null,
        booking_date: serviceForm.booking_date,
        start_time: serviceForm.start_time || timeSlots.find(t => {
          const [h] = t.split(':');
          return parseInt(h) >= new Date().getHours();
        }),
        therapy_type: serviceForm.therapy_type,
        therapy_duration: parseInt(serviceForm.therapy_duration),
        therapist_id: serviceForm.therapist_id || user.user_id,
        base_price: parseFloat(serviceForm.base_price) || 0,
        discount_percent: parseFloat(serviceForm.discount_percent) || 0,
        discount_amount: parseFloat(pricing.discountAmount),
        payment_type: serviceForm.payment_type,
        payment_status: serviceForm.payment_type === 'prepaid' ? 'paid' : 'pending',
        payment_mode: serviceForm.payment_mode,
        payment_received_by: serviceForm.payment_received_by,
        notes: serviceForm.notes
      };
      
      if (foundCustomer?.id) {
        // Update existing booking to checked_in
        await axios.post(`${API}/bookings/${foundCustomer.id}/check-in`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new booking
        await axios.post(`${API}/bookings`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success('Service entry recorded successfully!');
      setRecordServiceOpen(false);
      resetServiceForm();
      fetchData();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to record service');
      toast.error(errorMsg);
    }
  };

  // Helper function to extract error message from API response
  const extractErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (!detail) return fallback;
    // Handle Pydantic validation errors (array of objects)
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    // Handle string errors
    if (typeof detail === 'string') return detail;
    // Handle object errors
    if (typeof detail === 'object') return detail.msg || detail.message || fallback;
    return fallback;
  };

  // Submit future booking
  const handleFutureBooking = async () => {
    if (!futureForm.guest_name || !futureForm.guest_phone || !futureForm.booking_date || !futureForm.start_time || !futureForm.room_id) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const pricing = calculatePrice(futureForm.base_price, futureForm.discount_percent);
      
      const payload = {
        property_id: assignedProperty,
        room_id: futureForm.room_id,
        guest_name: futureForm.guest_name,
        guest_gender: futureForm.guest_gender,
        guest_phone: futureForm.guest_phone,
        guest_email: futureForm.guest_email,
        guest_type: futureForm.guest_type,
        booking_type: 'one_time',
        booking_date: futureForm.booking_date,
        start_time: futureForm.start_time,
        therapy_type: futureForm.therapy_type,
        therapy_duration: parseInt(futureForm.therapy_duration),
        therapist_id: futureForm.therapist_id === 'as_per_availability' ? null : futureForm.therapist_id,
        base_price: parseFloat(futureForm.base_price) || 0,
        discount_percent: parseFloat(futureForm.discount_percent) || 0,
        discount_amount: parseFloat(pricing.discountAmount),
        payment_type: futureForm.payment_type,
        payment_status: futureForm.payment_type === 'prepaid' ? 'paid' : 'pending',
        payment_mode: futureForm.payment_mode,
        payment_received_by: futureForm.payment_received_by,
        notes: futureForm.notes
      };
      
      await axios.post(`${API}/bookings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Future booking created successfully!');
      setFutureBookingOpen(false);
      resetFutureForm();
      fetchData();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to create booking');
      toast.error(errorMsg);
    }
  };

  // Handle reschedule
  const handleReschedule = async () => {
    if (!rescheduleForm.new_date || !rescheduleForm.new_time) {
      toast.error('Please select new date and time');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/bookings/${selectedBooking.id}/reschedule`,
        {
          new_date: rescheduleForm.new_date,
          new_time: rescheduleForm.new_time
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Booking rescheduled successfully');
      setRescheduleDialogOpen(false);
      fetchData();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to reschedule');
      toast.error(errorMsg);
    }
  };

  // Mark service as complete
  const handleCompleteService = async (booking) => {
    try {
      const token = localStorage.getItem('token');
      
      // If booking is not yet started, start it first
      if (booking.status === 'scheduled' || booking.status === 'checked_in') {
        await axios.post(`${API}/bookings/${booking.id}/start-therapy`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Complete the booking
      await axios.post(`${API}/bookings/${booking.id}/complete`, {
        payment_status: booking.payment_type === 'prepaid' ? 'paid' : booking.payment_status
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Service completed! Room is now in cleaning mode. Feedback email sent to customer.');
      fetchData();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to complete service');
      toast.error(errorMsg);
    }
  };

  // Mark booking as No Show
  const handleNoShow = async (booking) => {
    const isPrepaid = booking.payment_type === 'prepaid' || booking.package_id;
    const confirmMessage = isPrepaid 
      ? `This is a prepaid booking. Mark as No Show and call ${booking.guest_name} at ${booking.guest_phone} to reschedule?`
      : `Mark ${booking.guest_name}'s booking as No Show? They will need to make a new booking.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/bookings/${booking.id}/no-show`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.is_prepaid) {
        toast.success(`Marked as No Show. Please call ${response.data.guest_phone} to reschedule.`, {
          duration: 6000
        });
      } else {
        toast.success('Marked as No Show. Customer can make a new booking.');
      }
      
      fetchData();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to mark as no show');
      toast.error(errorMsg);
    }
  };

  // Change room status
  const handleRoomStatusChange = async (newStatus) => {
    if (!selectedRoom || !selectedSlot) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/rooms/${selectedRoom.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Room status changed to ${roomStatusLabels[newStatus]}`);
      setRoomStatusDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to change room status');
    }
  };

  // Quick mark room as available (for cleaning rooms)
  const handleQuickMarkAvailable = async (room) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/rooms/${room.id}/status`,
        { status: 'available' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${room.room_name} is now available!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to change room status');
    }
  };

  // Submit customer rating from service history
  const handleSubmitCustomerRating = async () => {
    if (!selectedServiceForRating) return;
    
    if (!ratingForm.overall_rating || !ratingForm.behavior_rating || 
        !ratingForm.punctuality_rating || !ratingForm.hygiene_rating ||
        ratingForm.would_serve_again === null) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const therapistId = verifiedTherapist?.user_id || user?.therapist_id || user?.user_id;
      
      await axios.post(`${API}/customer-ratings`, {
        customer_phone: selectedServiceForRating.guest_phone,
        customer_name: selectedServiceForRating.guest_name,
        therapist_id: therapistId,
        property_id: user?.assigned_property_id,
        booking_id: selectedServiceForRating.id,
        overall_rating: ratingForm.overall_rating,
        behavior_rating: ratingForm.behavior_rating,
        punctuality_rating: ratingForm.punctuality_rating,
        hygiene_rating: ratingForm.hygiene_rating,
        would_serve_again: ratingForm.would_serve_again,
        comment: ratingForm.comment || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Customer rating submitted!');
      setRateCustomerDialogOpen(false);
      setSelectedServiceForRating(null);
      setRatingForm({
        overall_rating: 0,
        behavior_rating: 0,
        punctuality_rating: 0,
        hygiene_rating: 0,
        would_serve_again: null,
        comment: ''
      });
      fetchData();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to submit rating');
      toast.error(errorMsg);
    }
  };

  // Create package
  const handleCreatePackage = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date();
      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + parseInt(packageForm.validity_days));
      
      const pricing = calculatePrice(packageForm.total_price, packageForm.discount_percent);
      
      const payload = {
        property_id: assignedProperty,
        package_name: packageForm.package_name,
        guest_name: packageForm.guest_name,
        guest_gender: packageForm.guest_gender,
        guest_phone: packageForm.guest_phone,
        guest_email: packageForm.guest_email || null,
        therapy_type: packageForm.therapy_type,
        session_duration: parseInt(packageForm.session_duration),
        total_sessions: parseInt(packageForm.total_sessions),
        base_price: parseFloat(packageForm.total_price),
        discount_percent: parseFloat(packageForm.discount_percent),
        discount_amount: parseFloat(pricing.discountAmount),
        gst_amount: parseFloat(pricing.gst),
        total_price: parseFloat(pricing.total),
        guest_type: packageForm.guest_type,
        therapist_id: packageForm.therapist_id === 'as_per_availability' ? null : packageForm.therapist_id,
        payment_received_by: (propertyDetails?.ownership_type === 'our_property' || propertyDetails?.ownership_type === 'owned_with_rent') ? 'nirvaana' : packageForm.payment_received_by,
        payment_mode: packageForm.payment_mode,
        valid_from: today.toISOString().split('T')[0],
        valid_until: validUntil.toISOString().split('T')[0]
      };
      
      await axios.post(`${API}/packages`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Package created successfully!');
      setPackageDialogOpen(false);
      resetPackageForm();
    } catch (error) {
      const errorMsg = extractErrorMessage(error, 'Failed to create package');
      toast.error(errorMsg);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (futureBookings.length === 0) {
      toast.error('No future bookings to export');
      return;
    }
    
    const headers = ['Booking Date', 'Time', 'Room', 'Guest Name', 'Phone', 'Email', 'Therapy', 'Duration', 'Type', 'Status', 'Base Price', 'Discount', 'GST', 'Total', 'Therapist', 'Payment Status', 'Payment Mode', 'Created At'];
    
    const rows = futureBookings.map(b => {
      const room = rooms.find(r => r.id === b.room_id);
      const therapist = therapists.find(t => t.user_id === b.therapist_id);
      return [
        b.booking_date,
        `${b.start_time} - ${b.end_time}`,
        room?.room_name || 'N/A',
        b.guest_name,
        b.guest_phone || '',
        b.guest_email || '',
        b.therapy_type,
        `${b.therapy_duration} mins`,
        b.booking_type,
        b.status,
        b.base_price || 0,
        b.discount_amount || 0,
        b.gst_amount || 0,
        b.total_amount || 0,
        therapist?.full_name || 'N/A',
        b.payment_status,
        b.payment_mode || '',
        b.created_at
      ].map(v => `"${v}"`).join(',');
    });
    
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `future_bookings_${assignedProperty}_${selectedDate}.csv`;
    link.click();
    
    toast.success('Bookings exported successfully');
  };

  // Reset forms
  const resetServiceForm = () => {
    setServiceForm({
      guest_name: '',
      guest_gender: '',
      guest_phone: '',
      guest_email: '',
      guest_type: 'outside',
      therapy_type: '',
      therapy_duration: 60,
      therapist_id: '',
      room_id: '',
      booking_date: new Date().toISOString().split('T')[0],
      start_time: '',
      base_price: '',
      discount_percent: '0',
      payment_type: 'postpaid',
      payment_mode: '',
      payment_received_by: '',
      notes: ''
    });
    setRecordStep(1);
    setSearchPhone('');
    setFoundCustomer(null);
    setFoundPackages([]);
    setSelectedPackage(null);
    setBookingType('one_time');
  };

  const resetFutureForm = () => {
    setFutureForm({
      guest_name: '',
      guest_gender: '',
      guest_phone: '',
      guest_email: '',
      guest_type: 'outside',
      therapy_type: '',
      therapy_duration: 60,
      therapist_id: 'as_per_availability',
      room_id: '',
      booking_date: '',
      start_time: '',
      base_price: '',
      discount_percent: '0',
      payment_type: 'postpaid',
      payment_mode: '',
      payment_received_by: '',
      notes: ''
    });
  };

  const resetPackageForm = () => {
    setPackageForm({
      package_name: '',
      guest_name: '',
      guest_gender: '',
      guest_phone: '',
      guest_email: '',
      therapy_type: 'TBD - Decide on arrival',
      total_sessions: '',
      session_duration: '60',
      total_price: '',
      validity_days: '90',
      guest_type: 'outside',
      therapist_id: 'as_per_availability',
      discount_percent: '0',
      payment_received_by: '',
      payment_mode: ''
    });
    setPackageStep(1);
  };

  // Get slot status for heatmap
  const getSlotStatus = (room, time) => {
    if (room.status === 'maintenance') return 'maintenance';
    if (room.status === 'cleaning') return 'cleaning';
    
    const booking = bookings.find(b => 
      b.room_id === room.id && 
      b.start_time <= time && 
      b.end_time > time &&
      b.status !== 'cancelled' && 
      b.status !== 'no_show'
    );
    
    return booking ? 'booked' : 'available';
  };

  // Date navigation
  const changeDate = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/therapist')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-serif text-foreground">Operations Dashboard</h1>
              {verifiedTherapist && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <User className="w-3 h-3" />
                  Logged in as: <span className="font-medium text-amber-600">{verifiedTherapist.full_name}</span>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="h-auto p-0 text-xs text-muted-foreground underline ml-2"
                    onClick={() => {
                      sessionStorage.removeItem('verified_therapist');
                      sessionStorage.removeItem('therapist_token');
                      setVerifiedTherapist(null);
                      setPinModalOpen(true);
                    }}
                  >
                    Switch User
                  </Button>
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPackageDialogOpen(true)}
              data-testid="create-package-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New Package
            </Button>
            <Button 
              size="sm"
              onClick={() => setRecordServiceOpen(true)}
              className={isSpaOpen ? "bg-green-600 hover:bg-green-700" : "bg-gray-400 cursor-not-allowed"}
              disabled={!isSpaOpen}
              title={!isSpaOpen ? `Spa is closed. Opens ${spaHoursDisplay}` : ''}
              data-testid="record-service-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> 
              {isSpaOpen ? 'Record New Service' : 'Spa Closed'}
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => setFutureBookingOpen(true)}
              data-testid="future-booking-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Future Booking
            </Button>
            <Button variant="outline" size="sm" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Tabs: Operations, Service History, Rate Customers, Feedback & Performance */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="operations" data-testid="operations-tab">
              <Calendar className="w-4 h-4 mr-2" /> Operations
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">
              <History className="w-4 h-4 mr-2" /> Service History
            </TabsTrigger>
            <TabsTrigger value="rate-customers" data-testid="rate-customers-tab">
              <Star className="w-4 h-4 mr-2" /> Rate Customers
            </TabsTrigger>
            <TabsTrigger value="feedback" data-testid="feedback-tab">
              <MessageSquare className="w-4 h-4 mr-2" /> Feedback
            </TabsTrigger>
            <TabsTrigger value="performance" data-testid="performance-tab">
              <Trophy className="w-4 h-4 mr-2" /> Performance
            </TabsTrigger>
            <TabsTrigger value="targets" data-testid="targets-tab">
              <Target className="w-4 h-4 mr-2" /> Targets
            </TabsTrigger>
          </TabsList>

          {/* Operations Tab */}
          <TabsContent value="operations" className="space-y-6">
            {/* Property Badge & Date Filter */}
            <div className="glass rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="bg-primary/10 text-primary mb-2">{assignedProperty}</Badge>
                  <p className="text-sm text-muted-foreground">
                    <Clock className="w-4 h-4 inline mr-1" />
                    {propertyDetails?.opening_time || '10:00'} - {propertyDetails?.closing_time || '20:00'} • {rooms.length} Room(s)
                  </p>
                </div>
                
                {/* Date Filter Only */}
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => changeDate(-1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                  />
                  <Button variant="outline" size="sm" onClick={() => changeDate(1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                  >
                    Today
                  </Button>
                </div>
              </div>
            </div>

            {/* Room Availability Heatmap */}
            <div className="glass rounded-2xl p-4">
              <h3 className="font-serif text-lg mb-4">Room Availability</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left py-2 px-3 text-sm text-muted-foreground sticky left-0 bg-white">Room</th>
                      {timeSlots.map(time => (
                        <th key={time} className="text-center py-2 px-1 text-xs text-muted-foreground min-w-[50px]">
                          {time}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map(room => (
                      <tr key={room.id} className="border-t">
                        <td className="py-2 px-3 text-sm font-medium sticky left-0 bg-white">
                          <div className="flex items-center gap-2">
                            {room.room_name}
                            {room.status === 'maintenance' && <Wrench className="w-3 h-3 text-gray-500" />}
                            {room.status === 'cleaning' && (
                              <button
                                onClick={() => {
                                  if (window.confirm(`Mark ${room.room_name} as available?`)) {
                                    handleQuickMarkAvailable(room);
                                  }
                                }}
                                className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200"
                                title="Click to mark as Available"
                              >
                                <Sparkles className="w-3 h-3 text-blue-500" />
                                <span>Mark Available</span>
                              </button>
                            )}
                          </div>
                        </td>
                        {timeSlots.map(time => {
                          const status = getSlotStatus(room, time);
                          const booking = bookings.find(b => 
                            b.room_id === room.id && 
                            b.start_time <= time && 
                            b.end_time > time &&
                            b.status !== 'cancelled' && 
                            b.status !== 'no_show'
                          );
                          
                          return (
                            <td key={time} className="py-1 px-1">
                              <button
                                onClick={() => {
                                  if (booking) {
                                    setSelectedBooking(booking);
                                    setBookingDetailsOpen(true);
                                  } else if (status === 'cleaning') {
                                    // Quick mark as available for cleaning rooms
                                    if (window.confirm('Cleaning done? Mark room as available?')) {
                                      handleQuickMarkAvailable(room);
                                    }
                                  } else {
                                    setSelectedRoom(room);
                                    setSelectedSlot(time);
                                    setRoomStatusDialogOpen(true);
                                  }
                                }}
                                className={`w-full h-8 rounded text-xs text-white flex items-center justify-center ${roomStatusColors[status]} hover:opacity-80`}
                                title={booking ? `${booking.guest_name} - ${booking.therapy_type}` : status === 'cleaning' ? 'Click to mark as Available' : roomStatusLabels[status]}
                              >
                                {status === 'available' && '+'}
                                {status === 'booked' && (booking?.guest_name?.substring(0, 6) || 'B')}
                                {status === 'cleaning' && <Sparkles className="w-3 h-3" />}
                                {status === 'maintenance' && <Wrench className="w-3 h-3" />}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Legend */}
              <div className="flex gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${roomStatusColors.available}`}></div>
                  <span>Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${roomStatusColors.booked}`}></div>
                  <span>Booked</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${roomStatusColors.cleaning}`}></div>
                  <span>Cleaning</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${roomStatusColors.maintenance}`}></div>
                  <span>Maintenance</span>
                </div>
              </div>
            </div>

            {/* Bookings Table */}
            <div className="glass rounded-2xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-lg">Bookings</h3>
                <Tabs value={bookingsTab} onValueChange={setBookingsTab}>
                  <TabsList>
                    <TabsTrigger value="today">Today ({bookings.filter(b => b.booking_date === selectedDate).length})</TabsTrigger>
                    <TabsTrigger value="future">Future</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {bookingsTab === 'today' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 text-sm text-muted-foreground">Time</th>
                        <th className="text-left py-2 px-2 text-sm text-muted-foreground">Guest</th>
                        <th className="text-left py-2 px-2 text-sm text-muted-foreground">Phone</th>
                        <th className="text-left py-2 px-2 text-sm text-muted-foreground">Room</th>
                        <th className="text-left py-2 px-2 text-sm text-muted-foreground">Therapy</th>
                        <th className="text-left py-2 px-2 text-sm text-muted-foreground">Status</th>
                        <th className="text-left py-2 px-2 text-sm text-muted-foreground">Type</th>
                        <th className="text-center py-2 px-2 text-sm text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.filter(b => b.booking_date === selectedDate && b.status !== 'completed').map(booking => {
                        const room = rooms.find(r => r.id === booking.room_id);
                        const statusConfig = {
                          scheduled: { bg: 'bg-yellow-50', badge: 'bg-yellow-100 text-yellow-700', label: 'Scheduled' },
                          checked_in: { bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700', label: 'Checked In' },
                          in_progress: { bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700', label: 'In Progress' },
                          completed: { bg: 'bg-green-50', badge: 'bg-green-100 text-green-700', label: 'Completed' },
                          cancelled: { bg: 'bg-red-50', badge: 'bg-red-100 text-red-700', label: 'Cancelled' },
                          no_show: { bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-700', label: 'No Show' }
                        };
                        const config = statusConfig[booking.status] || statusConfig.scheduled;
                        
                        return (
                          <tr 
                            key={booking.id} 
                            className={`border-b hover:bg-muted/30 cursor-pointer ${config.bg}`}
                            onClick={() => {
                              setSelectedBooking(booking);
                              setBookingDetailsOpen(true);
                            }}
                          >
                            <td className="py-2 px-2 text-sm">{booking.start_time} - {booking.end_time}</td>
                            <td className="py-2 px-2 text-sm font-medium">{booking.guest_name}</td>
                            <td className="py-2 px-2 text-sm">
                              <a href={`tel:${booking.guest_phone}`} className="text-primary hover:underline flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {booking.guest_phone}
                              </a>
                            </td>
                            <td className="py-2 px-2 text-sm">{room?.room_name || 'N/A'}</td>
                            <td className="py-2 px-2 text-sm">{booking.therapy_type}</td>
                            <td className="py-2 px-2 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.badge}`}>
                                {config.label}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-sm">
                              <Badge variant={booking.booking_type === 'package' ? 'default' : 'outline'}>
                                {booking.booking_type === 'package' ? 'Package' : 'One-time'}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <div className="flex gap-1 justify-center">
                                {(booking.status === 'scheduled' || booking.status === 'checked_in' || booking.status === 'in_progress') && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCompleteService(booking);
                                    }}
                                    className="h-7 text-xs"
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" /> Complete
                                  </Button>
                                )}
                                {booking.status === 'scheduled' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedBooking(booking);
                                      setRescheduleDialogOpen(true);
                                    }}
                                    className="h-7 text-xs"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                )}
                                {booking.status === 'scheduled' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNoShow(booking);
                                    }}
                                    className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {bookings.filter(b => b.booking_date === selectedDate).length === 0 && (
                        <tr>
                          <td colSpan="8" className="text-center py-8 text-muted-foreground">
                            No bookings for this date
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {bookingsTab === 'future' && (
                <div className="space-y-3">
                  <div className="flex justify-end">
                    <Button variant="outline" size="sm" onClick={exportToExcel}>
                      <Download className="w-4 h-4 mr-2" /> Export to Excel
                    </Button>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 text-sm font-medium">Date</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Time</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Guest</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Room</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Therapy</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Status</th>
                          <th className="text-left py-2 px-2 text-sm font-medium">Type</th>
                          <th className="text-center py-2 px-2 text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {futureBookings.map(booking => {
                          const room = rooms.find(r => r.id === booking.room_id);
                          return (
                            <tr key={booking.id} className="border-b hover:bg-muted/30">
                              <td className="py-2 px-2 text-sm font-medium">{booking.booking_date}</td>
                              <td className="py-2 px-2 text-sm">{booking.start_time} - {booking.end_time}</td>
                              <td className="py-2 px-2 text-sm">
                                <div className="font-medium">{booking.guest_name}</div>
                                <div className="text-xs text-muted-foreground">{booking.guest_phone}</div>
                              </td>
                              <td className="py-2 px-2 text-sm">{room?.room_name || 'N/A'}</td>
                              <td className="py-2 px-2 text-sm">{booking.therapy_type}</td>
                              <td className="py-2 px-2 text-sm">
                                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">
                                  {booking.status || 'scheduled'}
                                </span>
                              </td>
                              <td className="py-2 px-2 text-sm">
                                <Badge variant={booking.booking_type === 'package' ? 'default' : 'outline'}>
                                  {booking.booking_type === 'package' ? 'Package' : 'One-time'}
                                </Badge>
                              </td>
                              <td className="py-2 px-2 text-center">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedBooking(booking);
                                    setRescheduleDialogOpen(true);
                                  }}
                                  className="h-7 px-2"
                                >
                                  <Edit className="w-3 h-3 mr-1" /> Edit
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                        {futureBookings.length === 0 && (
                          <tr>
                            <td colSpan="8" className="text-center py-8 text-muted-foreground">
                              No future bookings
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Service History Tab */}
          <TabsContent value="history" className="space-y-6">
            <div className="glass rounded-2xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-lg">Service History</h3>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={serviceHistoryDate}
                    onChange={(e) => setServiceHistoryDate(e.target.value)}
                    className="w-40"
                  />
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 text-sm font-medium">Date</th>
                      <th className="text-left py-2 px-2 text-sm font-medium">Time</th>
                      <th className="text-left py-2 px-2 text-sm font-medium">Guest</th>
                      <th className="text-left py-2 px-2 text-sm font-medium">Therapy</th>
                      <th className="text-left py-2 px-2 text-sm font-medium">Amount</th>
                      <th className="text-left py-2 px-2 text-sm font-medium">Status</th>
                      <th className="text-center py-2 px-2 text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Combine bookings (completed/in_progress) AND services (direct recordings) */}
                    {(() => {
                      const currentTherapistId = verifiedTherapist?.user_id || user?.therapist_id || user?.user_id;
                      
                      // Get completed/in_progress bookings for this therapist
                      const historyFromBookings = bookings
                        .filter(b => {
                          // Service History shows ONLY completed services for this therapist
                          const isCompleted = b.status === 'completed';
                          const isForThisTherapist = b.therapist_id === currentTherapistId;
                          return isCompleted && isForThisTherapist;
                        })
                        .map(b => ({
                          id: b.id,
                          date: b.booking_date,
                          time: b.start_time,
                          guest_name: b.guest_name,
                          therapy_type: b.therapy_type,
                          total_amount: b.total_amount,
                          status: b.status,
                          customer_rated: b.customer_rated,
                          source: b.source || 'booking',
                          original: b
                        }));
                      
                      // No need for separate services query - all services now go through bookings
                      const allHistory = historyFromBookings
                        .sort((a, b) => {
                          const dateA = new Date(`${a.date}T${a.time || '00:00'}`);
                          const dateB = new Date(`${b.date}T${b.time || '00:00'}`);
                          return dateB - dateA;
                        });
                      
                      if (allHistory.length === 0) {
                        return (
                          <tr>
                            <td colSpan="7" className="text-center py-8 text-muted-foreground">
                              No completed services yet
                            </td>
                          </tr>
                        );
                      }
                      
                      return allHistory.map(service => (
                        <tr key={`${service.source}-${service.id}`} className="border-b hover:bg-muted/30">
                          <td className="py-2 px-2 text-sm font-medium">{service.date}</td>
                          <td className="py-2 px-2 text-sm">{service.time}</td>
                          <td className="py-2 px-2 text-sm">{service.guest_name}</td>
                          <td className="py-2 px-2 text-sm">{service.therapy_type}</td>
                          <td className="py-2 px-2 text-sm">₹{service.total_amount}</td>
                          <td className="py-2 px-2 text-sm">
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                              Completed
                            </span>
                            {service.source === 'record_new_service' && (
                              <span className="ml-1 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                                Walk-in
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {!service.customer_rated && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedServiceForRating(service.original);
                                    setRateCustomerDialogOpen(true);
                                  }}
                                  className="h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                >
                                  <Star className="w-3 h-3 mr-1" /> Rate
                                </Button>
                              )}
                              {service.customer_rated && (
                                <Badge variant="outline" className="text-xs text-green-600">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Rated
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Rate Customers Tab */}
          <TabsContent value="rate-customers" className="space-y-6">
            <RateYourCustomers 
              therapistId={verifiedTherapist?.user_id || verifiedTherapist?.id || user?.therapist_id || user?.user_id}
              therapistName={verifiedTherapist?.full_name || user?.full_name}
              propertyId={user?.assigned_property_id}
            />
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback" className="space-y-6">
            <TherapistFeedback 
              therapistId={verifiedTherapist?.user_id || verifiedTherapist?.id || user?.therapist_id || user?.user_id} 
              therapistName={verifiedTherapist?.full_name || user?.full_name}
            />
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <TherapistPerformanceTab 
              therapistId={verifiedTherapist?.user_id || verifiedTherapist?.id || user?.therapist_id || user?.user_id} 
              therapistName={verifiedTherapist?.full_name || user?.full_name}
              propertyId={user?.assigned_property_id}
            />
          </TabsContent>

          {/* Targets & Incentives Tab */}
          <TabsContent value="targets" className="space-y-6">
            <PerformanceDashboard 
              propertyId={assignedProperty}
              currentTherapistId={verifiedTherapist?.user_id || verifiedTherapist?.id || user?.therapist_id || user?.user_id}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* PIN Verification Modal */}
      <PinVerificationModal
        isOpen={pinModalOpen}
        onClose={() => {
          // If not verified, go back to dashboard
          if (!verifiedTherapist) {
            navigate('/therapist');
          }
          setPinModalOpen(false);
        }}
        onVerified={(therapist) => {
          setVerifiedTherapist(therapist);
          setPinModalOpen(false);
          toast.success(`Welcome, ${therapist.full_name}!`);
        }}
        propertyId={user?.assigned_property_id}
        propertyName={propertyDetails?.hotel_name || user?.assigned_property_id}
      />

      {/* Record New Service Dialog */}
      <Dialog open={recordServiceOpen} onOpenChange={(open) => {
        setRecordServiceOpen(open);
        if (!open) resetServiceForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Record New Service</DialogTitle>
            <DialogDescription>
              {recordStep === 1 ? 'Enter customer phone to search' : 'Fill service details'}
            </DialogDescription>
          </DialogHeader>
          
          {recordStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Customer Phone Number</Label>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    placeholder="Enter 10-digit phone number"
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                  />
                  <Button onClick={searchByPhone}>
                    <Search className="w-4 h-4 mr-2" /> Search
                  </Button>
                </div>
              </div>
              
              {/* Show today's pre-booked customers */}
              <div className="mt-4">
                <Label className="text-muted-foreground">Today's Pre-Booked Customers</Label>
                <div className="max-h-48 overflow-y-auto space-y-2 mt-2">
                  {bookings.filter(b => b.status === 'scheduled').map(booking => (
                    <button
                      key={booking.id}
                      onClick={() => {
                        setSearchPhone(booking.guest_phone);
                        setFoundCustomer(booking);
                        setBookingType(booking.booking_type);
                        setServiceForm({
                          ...serviceForm,
                          guest_name: booking.guest_name,
                          guest_gender: booking.guest_gender || '',
                          guest_phone: booking.guest_phone,
                          guest_email: booking.guest_email || '',
                          guest_type: booking.guest_type || 'outside',
                          therapy_type: booking.therapy_type,
                          therapy_duration: booking.therapy_duration,
                          room_id: booking.room_id,
                          start_time: booking.start_time,
                          base_price: booking.base_price?.toString() || '',
                          payment_type: booking.payment_type || 'prepaid'
                        });
                        setRecordStep(2);
                      }}
                      className="w-full p-3 border rounded-lg text-left hover:border-primary hover:bg-primary/5 transition-all"
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{booking.guest_name}</p>
                          <p className="text-sm text-muted-foreground">{booking.therapy_type}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{booking.start_time}</p>
                          <Badge variant="outline" className="text-xs">
                            {booking.booking_type === 'package' ? 'Package' : 'One-time'}
                          </Badge>
                        </div>
                      </div>
                    </button>
                  ))}
                  {bookings.filter(b => b.status === 'scheduled').length === 0 && (
                    <p className="text-center py-4 text-muted-foreground">No pre-booked customers for today</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {recordStep === 2 && (
            <div className="space-y-4">
              <Button variant="ghost" size="sm" onClick={() => setRecordStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Search
              </Button>

              {/* Status Banner - Shows what was found */}
              {selectedPackage && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-purple-900">{selectedPackage.package_name}</p>
                      <p className="text-sm text-purple-700">{selectedPackage.therapy_type}</p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-purple-200 text-purple-800 text-lg px-3 py-1">
                        Session {(selectedPackage.sessions_used || 0) + 1}/{selectedPackage.total_sessions}
                      </Badge>
                      <p className="text-xs text-purple-600 mt-1">
                        {selectedPackage.total_sessions - (selectedPackage.sessions_used || 0)} sessions remaining
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {foundCustomer && !selectedPackage && foundCustomer.booking_type === 'one_time' && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="font-medium text-blue-900">Pre-booked One-Time Appointment</p>
                  <p className="text-sm text-blue-700">Scheduled for {foundCustomer.booking_date} at {foundCustomer.start_time}</p>
                </div>
              )}

              {!foundCustomer && !selectedPackage && foundPackages.length === 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="font-medium text-amber-900">New Customer - One-Time Booking Only</p>
                  <p className="text-sm text-amber-700">
                    To create a package session, first create a package using "+New Package" button, then record the service.
                  </p>
                </div>
              )}

              {/* Booking Type - Only show if package exists but no pre-booking */}
              {foundPackages.length > 0 && !foundCustomer && (
                <div className="p-3 bg-muted/30 rounded-lg">
                  <Label className="mb-2 block">Booking Type</Label>
                  <RadioGroup
                    value={bookingType}
                    onValueChange={(val) => {
                      setBookingType(val);
                      if (val === 'one_time') {
                        setSelectedPackage(null);
                        setServiceForm({
                          ...serviceForm,
                          payment_type: 'postpaid',
                          base_price: ''
                        });
                      }
                    }}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="one_time" id="one_time_service" />
                      <Label htmlFor="one_time_service">One-Time Service</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="package" id="package_service" />
                      <Label htmlFor="package_service">Package Session</Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Fixed Booking Type display for pre-booked or new customer */}
              {(foundCustomer || (foundPackages.length === 0 && !selectedPackage)) && (
                <div className="p-3 bg-muted/30 rounded-lg">
                  <Label className="mb-2 block">Booking Type</Label>
                  <Badge className={bookingType === 'package' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                    {bookingType === 'package' ? 'Package Session' : 'One-Time Service'}
                  </Badge>
                  {foundPackages.length === 0 && !foundCustomer && (
                    <p className="text-xs text-muted-foreground mt-1">Package option disabled - no active package found</p>
                  )}
                </div>
              )}

              {/* Package Selection - Only when booking type is package and multiple packages exist */}
              {bookingType === 'package' && foundPackages.length > 1 && !foundCustomer && (
                <div>
                  <Label>Select Package</Label>
                  <div className="space-y-2 mt-2">
                    {foundPackages.map(pkg => {
                      const sessionsUsed = pkg.sessions_used || 0;
                      const sessionsRemaining = pkg.total_sessions - sessionsUsed;
                      return (
                        <button
                          key={pkg.id}
                          onClick={() => {
                            setSelectedPackage(pkg);
                            setServiceForm({
                              ...serviceForm,
                              guest_name: pkg.guest_name,
                              guest_gender: pkg.guest_gender || '',
                              guest_phone: pkg.guest_phone,
                              guest_email: pkg.guest_email || '',
                              therapy_type: pkg.therapy_type,
                              therapy_duration: pkg.session_duration || 60,
                              payment_type: 'prepaid',
                              base_price: '0'
                            });
                          }}
                          className={`w-full p-3 border rounded-lg text-left transition-all ${
                            selectedPackage?.id === pkg.id ? 'border-purple-500 bg-purple-50' : 'hover:border-purple-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-medium">{pkg.package_name}</span>
                              <p className="text-sm text-muted-foreground">{pkg.therapy_type}</p>
                            </div>
                            <Badge className="bg-purple-100 text-purple-700">
                              {sessionsUsed + 1}/{pkg.total_sessions} ({sessionsRemaining} left)
                            </Badge>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Guest Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Guest Name *</Label>
                  <Input
                    value={serviceForm.guest_name}
                    onChange={(e) => setServiceForm({ ...serviceForm, guest_name: e.target.value })}
                    disabled={!!foundCustomer}
                  />
                </div>
                <div>
                  <Label>Gender *</Label>
                  <Select
                    value={serviceForm.guest_gender}
                    onValueChange={(value) => setServiceForm({ ...serviceForm, guest_gender: value })}
                    disabled={!!foundCustomer}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={serviceForm.guest_phone}
                    onChange={(e) => setServiceForm({ ...serviceForm, guest_phone: e.target.value })}
                    disabled={!!foundCustomer}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={serviceForm.guest_email}
                    onChange={(e) => setServiceForm({ ...serviceForm, guest_email: e.target.value })}
                  />
                </div>
              </div>

              {/* Therapy & Room Assignment */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Therapy Type *</Label>
                  <Select
                    value={serviceForm.therapy_type}
                    onValueChange={(value) => {
                      const therapy = availableTherapies.find(t => t.name === value);
                      setServiceForm({ 
                        ...serviceForm, 
                        therapy_type: value,
                        therapy_duration: therapy?.duration || 60,
                        base_price: therapy?.base_price > 0 ? therapy.base_price.toString() : serviceForm.base_price
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select therapy" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTherapies.map(t => (
                        <SelectItem key={t.name} value={t.name}>
                          {t.name} ({t.duration} min){t.base_price > 0 ? ` - ₹${t.base_price}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Duration (mins)</Label>
                  <Input
                    type="number"
                    value={serviceForm.therapy_duration}
                    onChange={(e) => setServiceForm({ ...serviceForm, therapy_duration: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Assign Therapist *</Label>
                  <Select
                    value={serviceForm.therapist_id || user.user_id}
                    onValueChange={(value) => setServiceForm({ ...serviceForm, therapist_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {therapists.map(t => (
                        <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assign Room *</Label>
                  <Select
                    value={serviceForm.room_id}
                    onValueChange={(value) => setServiceForm({ ...serviceForm, room_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select room" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.filter(r => r.status === 'available').map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.room_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Time Slot */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={serviceForm.booking_date}
                    onChange={(e) => setServiceForm({ ...serviceForm, booking_date: e.target.value })}
                    disabled={!!foundCustomer}
                  />
                </div>
                <div>
                  <Label>Time Slot</Label>
                  <Select
                    value={serviceForm.start_time}
                    onValueChange={(value) => setServiceForm({ ...serviceForm, start_time: value })}
                    disabled={!!foundCustomer}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Base Price (₹) *</Label>
                  <Input
                    type="number"
                    value={serviceForm.base_price}
                    onChange={(e) => setServiceForm({ ...serviceForm, base_price: e.target.value })}
                    disabled={bookingType === 'package'}
                  />
                </div>
                <div>
                  <Label>Discount (%)</Label>
                  <Input
                    type="number"
                    value={serviceForm.discount_percent}
                    onChange={(e) => setServiceForm({ ...serviceForm, discount_percent: e.target.value })}
                    disabled={!!foundCustomer}
                  />
                </div>
              </div>

              {/* Price Summary */}
              {serviceForm.base_price && bookingType === 'one_time' && (
                <div className="p-3 bg-muted/30 rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span>Base Price</span>
                    <span>₹{servicePrice.base}</span>
                  </div>
                  {parseFloat(servicePrice.discountAmount) > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Discount ({servicePrice.discountPercent}%)</span>
                      <span>-₹{servicePrice.discountAmount}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>GST (5%)</span>
                    <span>₹{servicePrice.gst}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t pt-2 mt-2">
                    <span>Net Payable</span>
                    <span className="text-primary">
                      {serviceForm.payment_type === 'prepaid' ? '₹0 (Prepaid)' : `₹${servicePrice.total}`}
                    </span>
                  </div>
                </div>
              )}

              {bookingType === 'package' && selectedPackage && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-green-800">Package Session - No Payment Due</p>
                      <p className="text-sm text-green-700">{selectedPackage.package_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-800">
                        Session {(selectedPackage.sessions_used || 0) + 1}/{selectedPackage.total_sessions}
                      </p>
                      <p className="text-xs text-green-600">
                        Balance: {selectedPackage.total_sessions - (selectedPackage.sessions_used || 0) - 1} after this
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Type */}
              {bookingType === 'one_time' && (
                <div className="space-y-3">
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <Label className="mb-2 block">Payment Type</Label>
                    <RadioGroup
                      value={serviceForm.payment_type}
                      onValueChange={(value) => setServiceForm({ ...serviceForm, payment_type: value })}
                      className="flex gap-4"
                      disabled={!!foundCustomer}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="prepaid" id="prepaid_service" />
                        <Label htmlFor="prepaid_service">Prepaid</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="postpaid" id="postpaid_service" />
                        <Label htmlFor="postpaid_service">Postpaid</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {serviceForm.payment_type === 'postpaid' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Payment Received By</Label>
                        <Select
                          value={serviceForm.payment_received_by}
                          onValueChange={(value) => setServiceForm({ ...serviceForm, payment_received_by: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hotel">Hotel</SelectItem>
                            <SelectItem value="company">Your Company</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Payment Mode</Label>
                        <Select
                          value={serviceForm.payment_mode}
                          onValueChange={(value) => setServiceForm({ ...serviceForm, payment_mode: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="upi">UPI</SelectItem>
                            <SelectItem value="card">Card</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Button className="w-full" onClick={handleServiceEntry}>
                <ClipboardCheck className="w-4 h-4 mr-2" /> Click Service Entry
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Future Booking Dialog */}
      <Dialog open={futureBookingOpen} onOpenChange={(open) => {
        setFutureBookingOpen(open);
        if (!open) resetFutureForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Create Future Booking</DialogTitle>
            <DialogDescription>Book a one-time service for a future date</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-muted/30 rounded-lg">
              <Label className="mb-2 block">Booking Type</Label>
              <Badge>One-Time Service</Badge>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={futureForm.booking_date}
                  onChange={(e) => setFutureForm({ ...futureForm, booking_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-muted-foreground mt-1">Can book for today (later slots) or future dates</p>
              </div>
              <div>
                <Label>Guest Name *</Label>
                <Input
                  value={futureForm.guest_name}
                  onChange={(e) => setFutureForm({ ...futureForm, guest_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Gender *</Label>
                <Select
                  value={futureForm.guest_gender}
                  onValueChange={(value) => setFutureForm({ ...futureForm, guest_gender: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  value={futureForm.guest_phone}
                  onChange={(e) => setFutureForm({ ...futureForm, guest_phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={futureForm.guest_email}
                  onChange={(e) => setFutureForm({ ...futureForm, guest_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Guest Type</Label>
                <Select
                  value={futureForm.guest_type}
                  onValueChange={(value) => setFutureForm({ ...futureForm, guest_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_house">In-house Guest</SelectItem>
                    <SelectItem value="outside">Outside Guest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Therapy Type</Label>
                <Select
                  value={futureForm.therapy_type}
                  onValueChange={(value) => {
                    const therapy = availableTherapies.find(t => t.name === value);
                    setFutureForm({ 
                      ...futureForm, 
                      therapy_type: value,
                      therapy_duration: therapy?.duration || 60,
                      base_price: therapy?.base_price > 0 ? therapy.base_price.toString() : futureForm.base_price
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select therapy" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTherapies.map(t => (
                      <SelectItem key={t.name} value={t.name}>
                        {t.name} ({t.duration} min){t.base_price > 0 ? ` - ₹${t.base_price}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assign Therapist</Label>
                <Select
                  value={futureForm.therapist_id}
                  onValueChange={(value) => setFutureForm({ ...futureForm, therapist_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="as_per_availability">As per availability</SelectItem>
                    {therapists.map(t => (
                      <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time Slot *</Label>
                <Select
                  value={futureForm.start_time}
                  onValueChange={(value) => setFutureForm({ ...futureForm, start_time: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Room *</Label>
                <Select
                  value={futureForm.room_id}
                  onValueChange={(value) => setFutureForm({ ...futureForm, room_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.room_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Base Price (₹)</Label>
                <Input
                  type="number"
                  value={futureForm.base_price}
                  onChange={(e) => setFutureForm({ ...futureForm, base_price: e.target.value })}
                />
              </div>
              <div>
                <Label>Discount (%)</Label>
                <Input
                  type="number"
                  value={futureForm.discount_percent}
                  onChange={(e) => setFutureForm({ ...futureForm, discount_percent: e.target.value })}
                />
              </div>
            </div>

            {futureForm.base_price && (
              <div className="p-3 bg-muted/30 rounded-lg text-sm">
                <div className="flex justify-between">
                  <span>Base Price</span>
                  <span>₹{futurePrice.base}</span>
                </div>
                {parseFloat(futurePrice.discountAmount) > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Discount ({futurePrice.discountPercent}%)</span>
                    <span>-₹{futurePrice.discountAmount}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>GST (5%)</span>
                  <span>₹{futurePrice.gst}</span>
                </div>
                <div className="flex justify-between font-medium border-t pt-2 mt-2">
                  <span>Total</span>
                  <span className="text-primary">₹{futurePrice.total}</span>
                </div>
              </div>
            )}

            <div className="p-3 bg-muted/30 rounded-lg">
              <Label className="mb-2 block">Payment Type</Label>
              <RadioGroup
                value={futureForm.payment_type}
                onValueChange={(value) => setFutureForm({ ...futureForm, payment_type: value })}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="prepaid" id="prepaid_future" />
                  <Label htmlFor="prepaid_future">Prepaid</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="postpaid" id="postpaid_future" />
                  <Label htmlFor="postpaid_future">Postpaid</Label>
                </div>
              </RadioGroup>
            </div>

            {futureForm.payment_type === 'prepaid' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Received By</Label>
                  <Select
                    value={futureForm.payment_received_by}
                    onValueChange={(value) => setFutureForm({ ...futureForm, payment_received_by: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hotel">Hotel</SelectItem>
                      <SelectItem value="company">Your Company</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Mode</Label>
                  <Select
                    value={futureForm.payment_mode}
                    onValueChange={(value) => setFutureForm({ ...futureForm, payment_mode: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <Button className="w-full" onClick={handleFutureBooking}>
              Create Future Booking
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule Booking</DialogTitle>
            {selectedBooking && (
              <p className="text-sm text-muted-foreground">
                {selectedBooking.guest_name} - {selectedBooking.therapy_type}
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <p><strong>Current:</strong> {selectedBooking?.booking_date} at {selectedBooking?.start_time}</p>
            </div>
            
            <div>
              <Label>New Date *</Label>
              <Input
                type="date"
                value={rescheduleForm.new_date}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, new_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <Label>New Time *</Label>
              <Select
                value={rescheduleForm.new_time}
                onValueChange={(value) => setRescheduleForm({ ...rescheduleForm, new_time: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setRescheduleDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleReschedule}>
                Reschedule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Room Status Change Dialog */}
      <Dialog open={roomStatusDialogOpen} onOpenChange={setRoomStatusDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Room Status</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {selectedRoom?.room_name} at {selectedSlot}
            </p>
          </DialogHeader>
          
          <div className="space-y-2">
            <button
              onClick={() => handleRoomStatusChange('available')}
              className={`w-full p-3 rounded-lg flex items-center gap-3 ${roomStatusColors.available} text-white hover:opacity-90`}
            >
              <div className="w-4 h-4 rounded bg-white/30"></div>
              Available
            </button>
            <button
              onClick={() => handleRoomStatusChange('booked')}
              className={`w-full p-3 rounded-lg flex items-center gap-3 ${roomStatusColors.booked} text-white hover:opacity-90`}
            >
              <div className="w-4 h-4 rounded bg-white/30"></div>
              Booked
            </button>
            <button
              onClick={() => handleRoomStatusChange('cleaning')}
              className={`w-full p-3 rounded-lg flex items-center gap-3 ${roomStatusColors.cleaning} text-white hover:opacity-90`}
            >
              <Sparkles className="w-4 h-4" />
              Cleaning
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Details Dialog */}
      <Dialog open={bookingDetailsOpen} onOpenChange={setBookingDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <User className="w-5 h-5 text-primary" />
                  <span className="font-medium text-lg">{selectedBooking.guest_name}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    {selectedBooking.guest_phone}
                  </div>
                  {selectedBooking.guest_email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      {selectedBooking.guest_email}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Date & Time</p>
                  <p className="font-medium">{selectedBooking.booking_date} • {selectedBooking.start_time}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Therapy</p>
                  <p className="font-medium">{selectedBooking.therapy_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge variant={selectedBooking.status === 'completed' ? 'default' : 'secondary'}>
                    {selectedBooking.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Amount</p>
                  <p className="font-medium">₹{selectedBooking.total_amount}</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                {(selectedBooking.status === 'scheduled' || selectedBooking.status === 'checked_in' || selectedBooking.status === 'in_progress') && (
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700" 
                    onClick={() => {
                      handleCompleteService(selectedBooking);
                      setBookingDetailsOpen(false);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Mark Complete
                  </Button>
                )}
                {selectedBooking.status === 'scheduled' && (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setBookingDetailsOpen(false);
                      setRescheduleDialogOpen(true);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-2" /> Reschedule
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Package Creation Dialog - Same as Admin */}
      <Dialog open={packageDialogOpen} onOpenChange={(open) => {
        setPackageDialogOpen(open);
        if (!open) resetPackageForm();
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Create New Package {packageStep > 1 && `- Step ${packageStep}/3`}
            </DialogTitle>
            <div className="flex gap-2 mt-2">
              {[1, 2, 3].map(step => (
                <div 
                  key={step}
                  className={`h-2 flex-1 rounded-full ${step <= packageStep ? 'bg-primary' : 'bg-muted'}`}
                />
              ))}
            </div>
          </DialogHeader>
          
          {/* Step 1 */}
          {packageStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Package Name *</Label>
                <Input
                  value={packageForm.package_name}
                  onChange={(e) => setPackageForm({ ...packageForm, package_name: e.target.value })}
                  placeholder="e.g., 4-Session Relaxation Package"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Guest Name *</Label>
                  <Input
                    value={packageForm.guest_name}
                    onChange={(e) => setPackageForm({ ...packageForm, guest_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Gender *</Label>
                  <Select
                    value={packageForm.guest_gender}
                    onValueChange={(value) => setPackageForm({ ...packageForm, guest_gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={packageForm.guest_phone}
                    onChange={(e) => setPackageForm({ ...packageForm, guest_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={packageForm.guest_email}
                    onChange={(e) => setPackageForm({ ...packageForm, guest_email: e.target.value })}
                  />
                </div>
              </div>
              
              <div>
                <Label>Therapy Type *</Label>
                <Select
                  value={packageForm.therapy_type}
                  onValueChange={(value) => {
                    const therapy = availableTherapies.find(t => t.name === value);
                    setPackageForm({ 
                      ...packageForm, 
                      therapy_type: value,
                      session_duration: therapy?.duration?.toString() || packageForm.session_duration
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTherapies.map(t => (
                      <SelectItem key={t.name} value={t.name}>
                        {t.name} ({t.duration} min){t.base_price > 0 ? ` - ₹${t.base_price}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sessions *</Label>
                  <Input
                    type="number"
                    value={packageForm.total_sessions}
                    onChange={(e) => setPackageForm({ ...packageForm, total_sessions: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Session Duration</Label>
                  <Select
                    value={packageForm.session_duration}
                    onValueChange={(value) => setPackageForm({ ...packageForm, session_duration: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                      <SelectItem value="90">90 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Total Price (₹)</Label>
                  <Input
                    type="number"
                    value={packageForm.total_price}
                    onChange={(e) => setPackageForm({ ...packageForm, total_price: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Validity (days)</Label>
                  <Input
                    type="number"
                    value={packageForm.validity_days}
                    onChange={(e) => setPackageForm({ ...packageForm, validity_days: e.target.value })}
                  />
                </div>
              </div>
              
              <Button 
                className="w-full"
                onClick={() => {
                  if (!packageForm.package_name || !packageForm.guest_name || !packageForm.guest_phone || !packageForm.total_sessions || !packageForm.total_price) {
                    toast.error('Please fill all required fields');
                    return;
                  }
                  setPackageStep(2);
                }}
              >
                Next
              </Button>
            </div>
          )}

          {/* Step 2 */}
          {packageStep === 2 && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <Label>Booking Type</Label>
                <Badge className="ml-2">Package Session</Badge>
              </div>
              
              <div>
                <Label>Guest Type</Label>
                <Select
                  value={packageForm.guest_type}
                  onValueChange={(value) => setPackageForm({ ...packageForm, guest_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_house">In-house Guest</SelectItem>
                    <SelectItem value="outside">Outside Guest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Assign Therapist</Label>
                <Select
                  value={packageForm.therapist_id}
                  onValueChange={(value) => setPackageForm({ ...packageForm, therapist_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="as_per_availability">As per availability</SelectItem>
                    {therapists.map(t => (
                      <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Discount (%)</Label>
                <Input
                  type="number"
                  value={packageForm.discount_percent}
                  onChange={(e) => setPackageForm({ ...packageForm, discount_percent: e.target.value })}
                />
              </div>
              
              <div className="p-3 bg-green-50 rounded-lg">
                <Label className="text-green-700">Payment Type</Label>
                <Badge className="ml-2 bg-green-100 text-green-700">Prepaid</Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Received By</Label>
                  <Select
                    value={packageForm.payment_received_by}
                    onValueChange={(value) => setPackageForm({ ...packageForm, payment_received_by: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hotel">Hotel</SelectItem>
                      <SelectItem value="company">Nirvaana</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Mode</Label>
                  <Select
                    value={packageForm.payment_mode}
                    onValueChange={(value) => setPackageForm({ ...packageForm, payment_mode: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setPackageStep(1)}>
                  Previous
                </Button>
                <Button className="flex-1" onClick={() => setPackageStep(3)}>
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* Step 3 */}
          {packageStep === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <h4 className="font-medium">Package Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Package:</span>
                  <span>{packageForm.package_name}</span>
                  <span className="text-muted-foreground">Guest:</span>
                  <span>{packageForm.guest_name}</span>
                  <span className="text-muted-foreground">Therapy:</span>
                  <span>{packageForm.therapy_type}</span>
                  <span className="text-muted-foreground">Sessions:</span>
                  <span>{packageForm.total_sessions} × {packageForm.session_duration} mins</span>
                </div>
              </div>

              <div className="p-4 bg-primary/5 rounded-lg space-y-2">
                <h4 className="font-medium">Payment Details</h4>
                <div className="flex justify-between text-sm">
                  <span>Base Price</span>
                  <span>₹{packagePrice.base}</span>
                </div>
                {parseFloat(packagePrice.discountAmount) > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Discount ({packagePrice.discountPercent}%)</span>
                    <span>-₹{packagePrice.discountAmount}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span>GST (5%)</span>
                  <span>₹{packagePrice.gst}</span>
                </div>
                <div className="flex justify-between font-medium border-t pt-2 mt-2">
                  <span>Total</span>
                  <span className="text-primary">₹{packagePrice.total}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setPackageStep(2)}>
                  Previous
                </Button>
                <Button className="flex-1" onClick={handleCreatePackage}>
                  Create Package
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rate Customer Dialog */}
      <Dialog open={rateCustomerDialogOpen} onOpenChange={setRateCustomerDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif">Rate Customer</DialogTitle>
            <DialogDescription>
              Provide feedback for {selectedServiceForRating?.guest_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Customer Info */}
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <User className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="font-medium">{selectedServiceForRating?.guest_name}</p>
                <p className="text-sm text-muted-foreground">{selectedServiceForRating?.therapy_type}</p>
              </div>
            </div>
            
            {/* Overall Rating */}
            <div className="space-y-1">
              <Label className="text-sm font-medium">
                Overall Experience <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRatingForm({ ...ratingForm, overall_rating: star })}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star className={`w-6 h-6 ${star <= ratingForm.overall_rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>
            
            {/* Behavior Rating */}
            <div className="space-y-1">
              <Label className="text-sm font-medium">
                Behavior & Attitude <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRatingForm({ ...ratingForm, behavior_rating: star })}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star className={`w-6 h-6 ${star <= ratingForm.behavior_rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>
            
            {/* Punctuality Rating */}
            <div className="space-y-1">
              <Label className="text-sm font-medium">
                Punctuality <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRatingForm({ ...ratingForm, punctuality_rating: star })}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star className={`w-6 h-6 ${star <= ratingForm.punctuality_rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>
            
            {/* Hygiene Rating */}
            <div className="space-y-1">
              <Label className="text-sm font-medium">
                Hygiene <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRatingForm({ ...ratingForm, hygiene_rating: star })}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star className={`w-6 h-6 ${star <= ratingForm.hygiene_rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>
            
            {/* Would Serve Again */}
            <div className="space-y-2">
              <Label>
                Would you serve this customer again? <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={ratingForm.would_serve_again === true ? 'default' : 'outline'}
                  className={ratingForm.would_serve_again === true ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setRatingForm({ ...ratingForm, would_serve_again: true })}
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={ratingForm.would_serve_again === false ? 'default' : 'outline'}
                  className={ratingForm.would_serve_again === false ? 'bg-red-600 hover:bg-red-700' : ''}
                  onClick={() => setRatingForm({ ...ratingForm, would_serve_again: false })}
                >
                  <ThumbsDown className="w-4 h-4 mr-2" />
                  No
                </Button>
              </div>
            </div>
            
            {/* Comment */}
            <div className="space-y-2">
              <Label>Additional Comments (Optional)</Label>
              <Textarea
                placeholder="Any specific observations about this customer..."
                value={ratingForm.comment}
                onChange={(e) => setRatingForm({ ...ratingForm, comment: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setRateCustomerDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitCustomerRating}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Submit Rating
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TherapistOperations;
